import { Button, Card, Input, Spin } from "antd";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PendingVendor,
  RequestVendor,
  RetailerVendor,
  TeamManagement,
  userVendor,
} from "../../Elite/imageAssets";
import VendorOnboardingService from "../../../services/VendorOnboarding/vendorOnboarding-service";
import { NotificationManager } from "react-notifications";
import VendorDataTable from "./VendorDataTable";
import { SearchOutlined } from "@ant-design/icons";
import useEncryptData from "../../../utils/EncryptData";
import { useSelector } from "react-redux";

const PAGE_SIZE = 10;

// fields to search across
const SEARCH_FIELDS = [
  "vendorId",
  "vendorName",
  "vendorType",
  "saP_Code",
  "owner",
  "approvalStatus",
  "createdAt",
  "modifiedBy",
];

const normalize = (v) => (v == null ? "" : String(v).toLowerCase().trim());

const VendorManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const encryptData = useEncryptData();
  const vendorIdFromUrl = String(
    new URLSearchParams(location.search).get("vendorId") || ""
  ).trim();
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardCounts, setDashboardCounts] = useState({});
  const [vendorTableData, setVendorTableData] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [tableSearchData, setTableSearchData] = useState(vendorIdFromUrl);
  const [tileFilter, setTileFilter] = useState("ALL");

  const [tableLabel, setTableLabel] = useState("Vendor Data");
  const userDetails = useSelector(
    (state) => state.loginReducer.loginDetials.userDetails
  );
  const allowedCreateRoles = [
    "internal user",
    "admin",
    "finance team",
    "finance hod",
  ];
  const role = userDetails?.vendorRole?.toLowerCase();

  useEffect(() => {
    handleGetVendorDashboardCount();
  }, []);
  useEffect(() => {
    handleGetVendorTableData();
  }, [tileFilter]);
  useEffect(() => {
    if (!vendorIdFromUrl) return;
    setTableSearchData(vendorIdFromUrl);
    setCurrentPage(1);
  }, [vendorIdFromUrl]);

  const handleGetVendorDashboardCount = () => {
    setIsDashboardLoading(true);
    let inputData = {
      companyCode: localStorage.getItem("Company_Code"),
    };

    VendorOnboardingService.getVendorDashboardCountAPI(inputData)
      .then((response) => {
        if (response.data.statusCode === 200) {
          let finaldata = response.data.responseData;
          if (Object.keys(finaldata).length > 0) {
            setDashboardCounts(finaldata);
          } else {
            setDashboardCounts({});
          }
        } else {
          NotificationManager.error(response.data.message);
        }
      })
      .catch((error) => {
        setIsDashboardLoading(false);
        console.log(error, "Error");
      })
      .finally(() => {
        setIsDashboardLoading(false);
      });
  };

  const handleEditData = (record, mode) => {
    const encryptedMode = encodeURIComponent(encryptData(mode));
    navigate(
      `/empVendorDetails?vendorId=${record.vendorId}&mode=${encryptedMode}`
    );
  };

  const handleGetVendorTableData = () => {
    setIsTableLoading(true);
    const inputData = {
      companyCode: localStorage.getItem("Company_Code"),
      tileFilter: tileFilter,
    };
    VendorOnboardingService.getVendorTableDataAPI(inputData)
      .then((response) => {
        if (response.data.statusCode === 200) {
          const finaldata = response.data.responseData;

          setVendorTableData(Array.isArray(finaldata) ? finaldata : []);

          setCurrentPage(1);
        } else {
          NotificationManager.error(response.data.message);
          setVendorTableData([]);
        }
      })
      .catch((_e) => setVendorTableData([]))
      .finally(() => setIsTableLoading(false));
  };

  const filteredAndSorted = useMemo(() => {
    const safeData = Array.isArray(vendorTableData) ? vendorTableData : []; // ✅ Always array
    const q = normalize(tableSearchData);

    let arr = safeData.filter((row) => {
      if (!q) return true;
      return SEARCH_FIELDS.some((f) => normalize(row[f]).includes(q));
    });

    if (sortField && sortOrder) {
      const isDateField =
        sortField === "createdAt" || sortField === "modifiedAt";
      arr = arr.slice().sort((a, b) => {
        let av = a?.[sortField];
        let bv = b?.[sortField];
        if (isDateField) {
          av = new Date(av || 0).getTime();
          bv = new Date(bv || 0).getTime();
          return sortOrder === "ascend" ? av - bv : bv - av;
        }
        av = normalize(av);
        bv = normalize(bv);
        const cmp = av.localeCompare(bv);
        return sortOrder === "ascend" ? cmp : -cmp;
      });
    }

    return arr;
  }, [vendorTableData, tableSearchData, sortField, sortOrder]);

  // paginate (FE)
  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAndSorted.slice(start, start + PAGE_SIZE);
  }, [filteredAndSorted, currentPage]);

  const handleTableChange = (pagination, _filters, sorter) => {
    setCurrentPage(pagination.current || 1);

    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (s && s.field && s.order) {
      setSortField(s.field);
      setSortOrder(s.order);
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const handleTblSearchChange = (e) => {
    setTableSearchData(e.target.value);
    setCurrentPage(1);
  };

  const handleOnSearchData = (value) => {
    setTableSearchData(value);
    setCurrentPage(1);
  };

  const handleTileClick = (filter, cardTitle) => {
    setTileFilter(filter);
    setTableLabel(cardTitle);
  };

  return (
    <>
      <Fragment>
        <div className="VendorOnboarding app-content content">
          <div className="content-wrapper">
            <div className="content-body">
              <Spin size="large" spinning={isDashboardLoading}>
                <div className="row">
                  {/* Requested Vendors */}
                  <div
                    className="col-xl-2-4 col-lg-2-4 col-md-2-4 col-sm-12"
                    onClick={() =>
                      handleTileClick("Requested", "Requested Vendors Data")
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className={`card bg_success ${
                        tileFilter === "Requested" ? "active-tile" : ""
                      }`}
                    >
                      <div className="card-content">
                        <div className="card-body Vendor1 images_bg">
                          <h4 className="text-white font24">
                            <img
                              src={RequestVendor}
                              className="card_small_img"
                              alt="Img"
                            />
                            <div className="tile_cards">
                              <span className="card-title-text">
                                Requested Vendors
                              </span>
                              <label>
                                {dashboardCounts?.requestedCount ?? 0}
                              </label>
                            </div>
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pending with Internal User */}
                  <div
                    className="col-xl-2-4 col-lg-2-4 col-md-2-4 col-sm-12"
                    onClick={() =>
                      handleTileClick(
                        "Pending With Internal User",
                        "Pending with Internal User Data"
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className={`card bg_warning ${
                        tileFilter === "Pending With Internal User"
                          ? "active-tile"
                          : ""
                      }`}
                    >
                      <div className="card-content">
                        <div className="card-body Vendor2 images_bg">
                          <h4 className="text-white font24">
                            <img
                              src={userVendor}
                              className="card_small_img"
                              alt="Img"
                            />
                            <div className="tile_cards">
                              <span className="card-title-text">
                                Pending with Internal User
                              </span>
                              <label>
                                {dashboardCounts?.pendingWithInternalCount ?? 0}
                              </label>
                            </div>
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="col-xl-2-4 col-lg-2-4 col-md-2-4 col-sm-12"
                    onClick={() =>
                      handleTileClick(
                        "Pending With Finance Team",
                        "Pending with Finance Team Data"
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className={`card bg_danger ${
                        tileFilter === "Pending With Finance Team"
                          ? "active-tile"
                          : ""
                      }`}
                    >
                      <div className="card-content">
                        <div className="card-body Vendor3 images_bg ">
                          <h4
                            className="text-white font24"
                            style={{ textDecoration: "none" }}
                          >
                            <img
                              src={TeamManagement}
                              className="card_small_img"
                              alt="Img"
                            />
                            <div className="tile_cards">
                              <span className="card-title-text">
                                Pending with Finance Team
                              </span>
                              <label>
                                {dashboardCounts?.pendingWithFinanceCount ?? 0}
                              </label>
                            </div>
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="col-xl-2-4 col-lg-2-4 col-md-2-4 col-sm-12"
                    onClick={() =>
                      handleTileClick(
                        "Pending With Finance HOD",
                        "Pending With Finance HOD Data"
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className={`card bg_info ${
                        tileFilter === "Pending With Finance HOD"
                          ? "active-tile"
                          : ""
                      }`}
                    >
                      <div className="card-content">
                        <div className="card-body Vendor4 images_bg">
                          <h4 className="text-white font24">
                            <img
                              src={PendingVendor}
                              className="card_small_img"
                              alt="Img"
                            />
                            <div className="tile_cards">
                              <span className="card-title-text">
                                Pending with Finance HOD
                              </span>
                              <label>
                                {dashboardCounts?.pendingWithFinanceHODCount ??
                                  0}
                              </label>
                            </div>
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="col-xl-2-4 col-lg-2-4 col-md-2-4 col-sm-12"
                    onClick={() =>
                      handleTileClick("MyVendors", "My Vendors Data")
                    }
                  >
                    <div
                      className={`card bg_blue-vendor ${
                        tileFilter === "MyVendors" ? "active-tile" : ""
                      }`}
                    >
                      <div className="card-content">
                        <div className="card-body Vendor5 images_bg">
                          <h4 className="text-white font24">
                            <img
                              src={RetailerVendor}
                              className="card_small_img"
                              alt="Img"
                            />
                            <div className="tile_cards">
                              <div className="d-flex justify-content-between">
                                <div>
                                  {" "}
                                  <span className="card-title-text">
                                    Approved
                                  </span>{" "}
                                  <label>
                                    {dashboardCounts?.approvedCount ?? 0}
                                  </label>
                                </div>
                                <div>|</div>
                                <div>
                                  <span className="card-title-text">
                                    Rejected
                                  </span>
                                  <label>
                                    {dashboardCounts?.rejectedCount ?? 0}
                                  </label>
                                </div>
                              </div>
                            </div>
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Spin>

              <div className="table_heading_section mt-0">
                <div className="row payment_tab">
                  <label className="analy_type">{tableLabel}</label>
                </div>
                <div className="d-flex">
                  <div className="tab_searchtxt pb_0">
                    <Input
                      placeholder="Search..."
                      className="dash_search"
                      allowClear
                      size="large"
                      value={tableSearchData}
                      onChange={handleTblSearchChange}
                      onPressEnter={(e) =>
                        handleOnSearchData(e.currentTarget.value)
                      }
                      style={{ borderRadius: "18px" }}
                      suffix={
                        <SearchOutlined
                          onClick={() => handleOnSearchData(tableSearchData)}
                          style={{ cursor: "pointer" }}
                        />
                      }
                    />
                  </div>
                  <div className="pt-0 reset_save_btn justify-content-start">
                    {allowedCreateRoles.includes(role) && (
                      <Button
                        className="save_btn"
                        style={{ width: "fit-content" }}
                        onClick={() => navigate("/CreateVendor")}
                      >
                        Create Vendor
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <Card className="pay_section_2">
                <div className="row">
                  <div className="col-md-12">
                    <VendorDataTable
                      vendorTableData={pagedData}
                      total={filteredAndSorted.length}
                      pageSize={PAGE_SIZE}
                      currentPage={currentPage}
                      visitorLoading={isTableLoading}
                      onTableChange={handleTableChange}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleEditData={handleEditData}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </Fragment>
    </>
  );
};

export default VendorManagement;
